/**
 * CreateStoryPage - 创建剧情并生成初始视频
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { storyAPI } from '../services/storyAPI';
import { getVideoUrl } from '../config';
import SidebarNav from './common/SidebarNav';

const STORAGE_KEY = 'create_story_form_data';
const STORAGE_CREATED_ID_KEY = 'create_story_created_id';

const CreateStoryPage = () => {
  // 从 localStorage 恢复表单数据
  const loadFormData = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn('恢复表单数据失败:', e);
    }
    return { background: '' };
  };

  // 从 localStorage 恢复创建状态
  const loadCreatedState = () => {
    try {
      const savedId = localStorage.getItem(STORAGE_CREATED_ID_KEY);
      return savedId ? parseInt(savedId, 10) : null;
    } catch (e) {
      console.warn('恢复创建状态失败:', e);
      return null;
    }
  };

  const initialFormData = loadFormData();
  const [background, setBackground] = useState(initialFormData.background);
  const [characterFile, setCharacterFile] = useState(null);
  const [characterPreview, setCharacterPreview] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [createdId, setCreatedId] = useState(loadCreatedState());
  const [previewUrl, setPreviewUrl] = useState(null);
  const pollingRef = useRef(null);
  const navigate = useNavigate();

  // 表单验证：故事背景是必填项
  const disabled = !background.trim() || creating;

  // 当表单数据变化时保存到 localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        background,
      }));
    } catch (e) {
      console.warn('保存表单数据失败:', e);
    }
  }, [background]);

  const startPolling = (id) => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(async () => {
      try {
        const res = await storyAPI.getStoryLibraryItem(id);
        if (res?.data?.success) {
          const item = res.data.data;
          const first = Array.isArray(item?.history) && item.history.length > 0 ? item.history[0] : null;
          const url = first?.video_url || null;
          if (url) {
            const finalUrl = url.startsWith('/static') ? url : getVideoUrl(url.split('/').pop());
            setPreviewUrl(finalUrl);
            stopPolling();
            setCreating(false);
            // 创建已完成：清理本地持久化，避免下次进入仍显示上一次创建
            try {
              localStorage.removeItem(STORAGE_KEY);
              localStorage.removeItem(STORAGE_CREATED_ID_KEY);
            } catch {}
            setCreatedId(null);
          }
        }
      } catch (e) {
        // 忽略轮询错误，继续下一轮
      }
    }, 6000);
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  useEffect(() => () => stopPolling(), []);

  // 组件挂载时，如果有 createdId，检查是否需要恢复状态
  useEffect(() => {
    const checkAndRestoreState = async () => {
      if (createdId) {
        try {
          // 检查故事是否已创建，以及视频是否已生成
          const res = await storyAPI.getStoryLibraryItem(createdId);
          if (res?.data?.success) {
            const item = res.data.data;
            const first = Array.isArray(item?.history) && item.history.length > 0 ? item.history[0] : null;
            const url = first?.video_url || null;
            if (url) {
              // 视频已生成，恢复预览状态
              const finalUrl = url.startsWith('/static') ? url : getVideoUrl(url.split('/').pop());
              setPreviewUrl(finalUrl);
              setCreating(false);
              // 创建已完成：清理本地持久化，避免下次进入仍显示上一次创建
              try {
                localStorage.removeItem(STORAGE_KEY);
                localStorage.removeItem(STORAGE_CREATED_ID_KEY);
              } catch {}
              setCreatedId(null);
            } else {
              // 视频还在生成中，恢复轮询
              setCreating(true);
              startPolling(createdId);
            }
          }
        } catch (e) {
          // 如果查询失败，可能是故事不存在，清除 createdId
          console.warn('恢复状态失败:', e);
          setCreatedId(null);
          localStorage.removeItem(STORAGE_CREATED_ID_KEY);
        }
      }
    };

    checkAndRestoreState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 只在组件挂载时执行一次

  const handleCreate = async () => {
    setError(null);
    setCreating(true);
    setPreviewUrl(null);
    // 不重置 createdId，因为可能之前已经创建过
    try {
      // 使用 FormData 方式提交，支持文件上传
      const formData = new FormData();
      formData.append('story_background', background.trim());
      
      // 如果选择了角色图片，添加到表单中
      if (characterFile) {
        formData.append('character_image', characterFile);
      }
      
      console.log('提交的故事数据:', {
        story_background: background.trim(),
        character_image: characterFile ? characterFile.name : '未选择'
      });
      
      // 提交创建请求，会自动触发生成开场视频
      const res = await storyAPI.createStoryLibraryItem(formData, { generate_initial_video: true, sync: false });
      if (res?.data?.success) {
        const id = res.data.data.id;
        setCreatedId(id);
        // 保存 createdId 到 localStorage
        localStorage.setItem(STORAGE_CREATED_ID_KEY, id.toString());
        // 开始轮询检查视频生成状态
        startPolling(id);
      } else {
        throw new Error('创建失败');
      }
    } catch (e) {
      setCreating(false);
      setError(e.response?.data?.detail || e.message);
    }
  };

  const handlePickFile = (e) => {
    const file = e.target.files && e.target.files[0];
    setCharacterFile(file || null);
    if (file) {
      const url = URL.createObjectURL(file);
      setCharacterPreview(url);
    } else {
      setCharacterPreview(null);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleStart = async () => {
    if (!createdId) return;
    try {
      const res = await storyAPI.initStoryFromLibrary(createdId, null);
      if (res?.data?.success && res.data.session_id) {
        // 清除保存的状态，因为已经开始了故事
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_CREATED_ID_KEY);
        navigate(`/story/session/${res.data.session_id}`);
      } else {
        throw new Error('初始化失败');
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  // 清除保存的数据（可选：添加一个重置按钮）
  const handleClearSaved = () => {
    if (window.confirm('确定要清除保存的创作进度吗？')) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_CREATED_ID_KEY);
      setBackground('');
      setCharacterFile(null);
      setCharacterPreview(null);
      setCreatedId(null);
      setPreviewUrl(null);
      setCreating(false);
      setError(null);
      stopPolling();
    }
  };

  return (
    <div className="App">
      <SidebarNav />
      <div className="main-layout">
        <div className="content-area" style={{ height: '100vh', overflow: 'hidden' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: 24, 
            height: '100%',
            padding: '24px',
            maxWidth: '1400px',
            margin: '0 auto'
          }}>
            {/* 左侧：表单区域 */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              height: '100%',
              paddingRight: 12,
              overflow: 'hidden'
            }}>
              <div style={{ marginBottom: 20, flexShrink: 0 }}>
                <h1 style={{ fontSize: 24, margin: 0, marginBottom: 4 }}>创建剧情</h1>
                <p style={{ color: '#666', fontSize: 14, margin: 0 }}>填写故事背景信息并自动生成首段预览视频</p>
              </div>

              {error && (
                <div className="error" style={{ marginBottom: 16, fontSize: 14, padding: 12, flexShrink: 0 }}>{error}</div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0, overflow: 'auto' }}>
                <div>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: 6, fontSize: 14 }}>故事背景 *</label>
                  <textarea 
                    value={background} 
                    onChange={(e) => setBackground(e.target.value)} 
                    rows={6} 
                    placeholder="描述整体的世界观和故事背景设定（必填）" 
                    style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 6, resize: 'none', fontSize: 14 }} 
                  />
                  <div style={{ color: '#888', fontSize: 11, marginTop: 4 }}>请详细描述您的故事背景，系统将根据此生成剧情。</div>
                </div>
                <div>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: 6, fontSize: 14 }}>参考角色图片（可选）</label>
                  <input type="file" accept="image/*" onChange={handlePickFile} style={{ fontSize: 14 }} />
                  {characterPreview && (
                    <div style={{ marginTop: 8 }}>
                      <img src={characterPreview} alt="角色预览" style={{ width: 120, height: 'auto', borderRadius: 6, border: '1px solid #eee' }} />
                    </div>
                  )}
                  <div style={{ color: '#888', fontSize: 11, marginTop: 4 }}>上传角色参考图片，用于生成视频中的角色形象。</div>
                </div>
              </div>
              
              {/* 按钮区域固定在底部 */}
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', paddingTop: 12, flexShrink: 0 }}>
                <button 
                  disabled={disabled} 
                  onClick={handleCreate} 
                  className="btn btn-primary" 
                  style={{ padding: '10px 18px', fontWeight: 600, fontSize: 14 }}
                >
                  {creating ? '⏳ 生成中…' : '🚀 创建并生成预览'}
                </button>
                <button 
                  onClick={() => navigate('/story')} 
                  style={{ padding: '10px 16px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: 14 }}
                >
                  返回
                </button>
                {(background || characterFile || createdId) && (
                  <button 
                    onClick={handleClearSaved} 
                    style={{ padding: '10px 16px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', color: '#666', fontSize: 14 }} 
                    title="清除保存的创作进度"
                  >
                    清除
                  </button>
                )}
              </div>
            </div>

            {/* 右侧：预览区域 */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              height: '100%',
              borderLeft: '1px solid #eee',
              paddingLeft: 24
            }}>
              <h3 style={{ fontSize: 18, margin: '0 0 16px 0', flexShrink: 0 }}>开场视频预览</h3>
              <div style={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: createdId ? 'flex-start' : 'center',
                alignItems: 'center',
                minHeight: 0,
                overflow: 'hidden'
              }}>
                {createdId ? (
                  <>
                    {previewUrl ? (
                      <video 
                        src={previewUrl} 
                        style={{ 
                          width: '100%', 
                          maxHeight: '90vh',
                          borderRadius: 8, 
                          background: '#000',
                          objectFit: 'contain'
                        }} 
                        controls 
                      />
                    ) : (
                      <div style={{ 
                        padding: 24, 
                        borderRadius: 8, 
                        border: '1px solid #eee', 
                        background: '#fafafa',
                        textAlign: 'center',
                        width: '100%'
                      }}>
                        <div style={{ fontSize: 16, marginBottom: 8 }}>⏳ 正在生成视频</div>
                        <div style={{ color: '#666', fontSize: 13 }}>这可能需要 1-2 分钟，请稍候…</div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ 
                    color: '#999', 
                    fontSize: 14, 
                    textAlign: 'center',
                    padding: 40
                  }}>
                    创建剧情后，预览视频将显示在这里
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateStoryPage;


